import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, IconButton, Paper, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatDateBR } from "./BillingPanel";

// Identifica o login atual pelo "iat" do JWT (claim pública, sem segredo). Cada login
// tem um iat diferente, então o aviso fechado volta no próximo login.
const DISMISS_PREFIX = "billingWarningDismissed:";

const getLoginStamp = () => {
  try {
    const raw = localStorage.getItem("token");
    if (!raw) return null;
    const jwt = String(JSON.parse(raw));
    const segment = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = segment + "=".repeat((4 - (segment.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload.iat ? String(payload.iat) : null;
  } catch (err) {
    return null;
  }
};

const isWarningDismissed = () => {
  const stamp = getLoginStamp();
  if (!stamp) return false;
  try {
    return sessionStorage.getItem(DISMISS_PREFIX + stamp) === "1";
  } catch (err) {
    return false;
  }
};

const dismissWarning = () => {
  const stamp = getLoginStamp();
  if (!stamp) return;
  try {
    sessionStorage.setItem(DISMISS_PREFIX + stamp, "1");
  } catch (err) {
    // storage indisponível: o aviso apenas continua visível
  }
};

/**
 * Bloqueio e aviso de assinatura. A decisão vem do backend (/billing/status e
 * do código 402 que o isAuth devolve quando a empresa está em atraso).
 *  - Atraso (D+1): tela bloqueando tudo. Admin vai para pagamento; demais saem
 *    e são direcionados ao login de administrador.
 *  - Aviso (D-5 a D): faixa fixa para o admin, com atalho para pagar. Pode ser
 *    fechada; volta no próximo login.
 */
const BillingGate = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, handleLogout } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [, setDismissTick] = useState(0);

  const onBillingRoute = location.pathname.startsWith("/billing");
  const isAdmin = user?.profile === "admin" || user?.profile === "super_admin" || !!user?.super;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (err) {
      // 402 é tratado pelo evento billing:blocked
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const timer = setInterval(loadStatus, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [loadStatus]);

  useEffect(() => {
    const onBlocked = () => setStatus((prev) => ({ ...(prev || {}), overdue: true, state: "overdue" }));
    window.addEventListener("billing:blocked", onBlocked);
    return () => window.removeEventListener("billing:blocked", onBlocked);
  }, []);

  const blocked = !!status?.overdue && !onBillingRoute;

  if (blocked) {
    const goToLoginAsAdmin = async () => {
      try {
        await handleLogout();
      } catch (err) {
        // sessão já inválida: segue para o login mesmo assim
      }
      localStorage.removeItem("token");
      history.push("/login?cobranca=1");
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2000,
          background: "rgba(15, 23, 42, 0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16
        }}
      >
        <Paper style={{ maxWidth: 440, width: "100%", padding: 28, borderRadius: 16, textAlign: "center" }} elevation={0}>
          <Typography variant="h5" style={{ fontWeight: 800, marginBottom: 8 }}>
            Acesso bloqueado por falta de pagamento
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 20 }}>
            Vencimento em {formatDateBR(status?.dueDate)}. O sistema será liberado para todos os usuários
            assim que o pagamento via PIX for confirmado.
          </Typography>
          {isAdmin ? (
            <Button variant="contained" color="primary" fullWidth onClick={() => history.push("/billing/pay")}>
              Pagar agora (PIX)
            </Button>
          ) : (
            <>
              <Button variant="contained" color="primary" fullWidth onClick={goToLoginAsAdmin}>
                Solicitar pagamento
              </Button>
              <Typography variant="caption" display="block" style={{ marginTop: 12 }}>
                Somente o administrador da conta pode efetuar o pagamento.
              </Typography>
            </>
          )}
        </Paper>
      </div>
    );
  }

  const showWarning = !!status?.warning && isAdmin && !onBillingRoute && !isWarningDismissed();
  const closeWarning = () => {
    dismissWarning();
    setDismissTick((tick) => tick + 1);
  };
  if (showWarning) {
    return (
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1500,
          background: "#f59e0b",
          color: "#111",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          fontSize: 14
        }}
      >
        <span>
          <strong>
            Sua assinatura vence {status.daysToDue === 0 ? "hoje" : `em ${status.daysToDue} dia(s)`}
          </strong>{" "}
          ({formatDateBR(status.dueDate)}). Pague via PIX para evitar o bloqueio.
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => history.push("/billing/pay")}
            style={{ background: "#111", color: "#fff", borderRadius: 20 }}
          >
            Pagar agora
          </Button>
          <IconButton size="small" aria-label="Fechar aviso" onClick={closeWarning} style={{ color: "#111" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  return null;
};

export default BillingGate;
